import { useState } from "react";
import { MapPin, Phone, MessageCircle, Send, Clock, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Reveal } from "@/components/motion";
import { submitLead } from "@/lib/api";
import { BRAND, PROJECT_TYPES, BUDGETS } from "@/lib/data";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", project_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone."); return; }
    setLoading(true);
    try {
      await submitLead({ ...form, source: "contact_section" });
      toast.success("Enquiry sent! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", project_type: "", budget: "", message: "" });
    } catch { toast.error("Something went wrong. Please try WhatsApp."); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-background" data-testid="contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
        <div>
          <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3">Contact</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-5xl font-bold mb-6 leading-tight">Let&apos;s design your dream space</h2></Reveal>
          <Reveal delay={0.1}><p className="text-muted-foreground mb-8 max-w-md">Book a free consultation with our design experts. We&apos;ll bring the studio to your home.</p></Reveal>

          <div className="space-y-4 mb-8">
            {[
              { icon: MapPin, label: BRAND.address, href: BRAND.mapLink },
              { icon: Phone, label: BRAND.phoneIntl, href: `tel:${BRAND.phone}` },
              { icon: Mail, label: BRAND.email, href: `mailto:${BRAND.email}` },
              { icon: MessageCircle, label: "Chat on WhatsApp", href: `https://wa.me/${BRAND.whatsapp}` },
              { icon: Clock, label: "Mon – Sat · 10:00 AM – 8:00 PM" },
            ].map((c, i) => (
              <Reveal key={i} delay={0.12 + i * 0.05}>
                <a href={c.href} target={c.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                  className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><c.icon className="w-5 h-5 text-primary" /></span>
                  <span className="text-sm group-hover:text-primary transition-colors">{c.label}</span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="rounded-2xl overflow-hidden border border-border h-64">
              <iframe title="map" src={BRAND.mapEmbed} width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="bg-card border border-border rounded-3xl p-8 shadow-lg" data-testid="contact-form">
            <h3 className="font-display text-2xl mb-6">Request a Free Quote</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Name*</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></div>
              <div className="space-y-1.5"><Label>Phone*</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile" /></div>
            </div>
            <div className="space-y-1.5 mt-4"><Label>Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div className="space-y-1.5"><Label>Project Type</Label>
                <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                  <SelectTrigger data-testid="contact-project-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PROJECT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Budget</Label>
                <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                  <SelectTrigger data-testid="contact-budget"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5 mt-4"><Label>Message</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="Tell us about your project" /></div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button type="submit" disabled={loading} data-testid="contact-submit" className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90 h-11">
                <Send className="w-4 h-4 mr-2" /> {loading ? "Sending..." : "Send Enquiry"}
              </Button>
              <Button asChild type="button" variant="outline" className="rounded-full h-11 border-primary/30">
                <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</a>
              </Button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
